import { z } from 'zod'
import { ageOn } from './people.ts'
import type { RawFieldValue } from './custom-fields.ts'

/**
 * Saved lists (segments): a rule tree, persisted as JSON, that both sides can
 * execute.
 *
 * There are two executors — `evaluateSegment` here, and the SQL compiler in
 * the API's lists module — and Phase 2's acceptance criterion is that they
 * agree exactly. That is only achievable if the *semantics* live in one place,
 * so this file is that place: every operator's meaning is defined by the
 * evaluator below, and the compiler is written to match it, with a test that
 * runs both over the same seeded people and compares.
 *
 * The persisted shape is `{ all: [...] }` / `{ any: [...] }` / `{ not: … }`
 * with leaf conditions, per 02-PHASES.md. Nodes discriminate on key presence
 * rather than a `type` tag: it keeps hand-written rules and the JSON a builder
 * produces readable, which matters when someone has to debug why a list has
 * 3,000 people in it.
 */

// ---- shape -------------------------------------------------------------------

export const SEGMENT_FIELDS = [
  'firstName',
  'lastName',
  'membershipStatus',
  'campusId',
  'birthdate',
  'age',
  'createdAt',
  'email',
  'phone',
  'tag',
  /** A custom field; `fieldId` says which. */
  'custom',
] as const
export type SegmentField = (typeof SEGMENT_FIELDS)[number]

export const SEGMENT_OPERATORS = [
  'eq',
  'neq',
  'in',
  'contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'isEmpty',
  'isNotEmpty',
  /** Tags and multiselect custom fields: "carries this one". */
  'has',
  'isTrue',
  'isFalse',
] as const
export type SegmentOperator = (typeof SEGMENT_OPERATORS)[number]

export interface SegmentRange {
  from?: string | number
  to?: string | number
}

export type SegmentValue = string | number | boolean | string[] | SegmentRange

export interface SegmentCondition {
  field: SegmentField
  /** Required when `field` is 'custom'. */
  fieldId?: string
  op: SegmentOperator
  value?: SegmentValue
}

export type SegmentNode =
  { all: SegmentNode[] } | { any: SegmentNode[] } | { not: SegmentNode } | SegmentCondition

export const isAllNode = (node: SegmentNode): node is { all: SegmentNode[] } => 'all' in node
export const isAnyNode = (node: SegmentNode): node is { any: SegmentNode[] } => 'any' in node
export const isNotNode = (node: SegmentNode): node is { not: SegmentNode } => 'not' in node
export const isCondition = (node: SegmentNode): node is SegmentCondition =>
  !isAllNode(node) && !isAnyNode(node) && !isNotNode(node)

// ---- validation --------------------------------------------------------------

const rangeSchema = z.object({
  from: z.union([z.string(), z.number()]).optional(),
  to: z.union([z.string(), z.number()]).optional(),
})

const conditionSchema = z
  .object({
    field: z.enum(SEGMENT_FIELDS),
    fieldId: z.uuid().optional(),
    op: z.enum(SEGMENT_OPERATORS),
    value: z
      .union([z.string(), z.number(), z.boolean(), z.array(z.string()), rangeSchema])
      .optional(),
  })
  .refine((c) => c.field !== 'custom' || c.fieldId !== undefined, {
    message: 'A custom-field condition needs a fieldId',
    path: ['fieldId'],
  })
  .refine((c) => !NEEDS_VALUE.has(c.op) || c.value !== undefined, {
    message: 'This operator needs a value',
    path: ['value'],
  })

const NEEDS_VALUE = new Set<SegmentOperator>([
  'eq',
  'neq',
  'in',
  'contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'has',
])

/**
 * Depth is capped: the tree is compiled into nested SQL, and an unbounded one
 * is a denial-of-service vector wearing a builder UI.
 */
const MAX_DEPTH = 5
const MAX_CONDITIONS = 100

export const segmentNodeSchema: z.ZodType<SegmentNode> = z.lazy(() =>
  z.union([
    // An empty group is legal and means "everyone" — the starting state of a
    // builder, and the rules of a list that has not been narrowed yet.
    z.object({ all: z.array(segmentNodeSchema).max(20) }),
    z.object({ any: z.array(segmentNodeSchema).max(20) }),
    z.object({ not: segmentNodeSchema }),
    conditionSchema,
  ]),
)

export const segmentRulesSchema = segmentNodeSchema
  .refine((node) => depthOf(node) <= MAX_DEPTH, `Nest at most ${MAX_DEPTH} levels deep`)
  .refine((node) => countConditions(node) <= MAX_CONDITIONS, `Use at most ${MAX_CONDITIONS} rules`)

export function depthOf(node: SegmentNode): number {
  if (isCondition(node)) return 1
  if (isNotNode(node)) return 1 + depthOf(node.not)
  const children = isAllNode(node) ? node.all : node.any
  return 1 + Math.max(0, ...children.map(depthOf))
}

export function countConditions(node: SegmentNode): number {
  if (isCondition(node)) return 1
  if (isNotNode(node)) return countConditions(node.not)
  const children = isAllNode(node) ? node.all : node.any
  return children.reduce((sum, child) => sum + countConditions(child), 0)
}

/** An empty group means "everyone" — the starting state of a builder. */
export const EMPTY_SEGMENT: SegmentNode = { all: [] }

export const listSchema = z.object({
  name: z.string().trim().min(1, 'Name the list').max(120),
  description: z
    .string()
    .trim()
    .max(280)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  rules: segmentRulesSchema,
})
export type ListInput = z.infer<typeof listSchema>
export type ListFormInput = z.input<typeof listSchema>

// ---- evaluation --------------------------------------------------------------

/**
 * Everything the evaluator needs about one person. Built from an API row on
 * the client, and from the database in the compiler-agreement test.
 */
export interface PersonSnapshot {
  firstName: string
  lastName: string
  membershipStatus: string
  campusId: string | null
  birthdate: string | null
  createdAt: string
  emails: string[]
  phones: string[]
  tagIds: string[]
  /** Custom field values by field id. */
  custom: Record<string, RawFieldValue>
}

const asArray = (value: SegmentValue | undefined): string[] =>
  Array.isArray(value) ? value.map(String) : value === undefined ? [] : [String(value)]

const asRange = (value: SegmentValue | undefined): SegmentRange =>
  value !== undefined && typeof value === 'object' && !Array.isArray(value) ? value : {}

const lower = (value: unknown) => String(value ?? '').toLowerCase()

/** Values a condition compares against, per field. */
function operandsOf(condition: SegmentCondition, person: PersonSnapshot): RawFieldValue[] {
  switch (condition.field) {
    case 'firstName':
      return [person.firstName]
    case 'lastName':
      return [person.lastName]
    case 'membershipStatus':
      return [person.membershipStatus]
    case 'campusId':
      return [person.campusId]
    case 'birthdate':
      return [person.birthdate]
    case 'createdAt':
      return [person.createdAt]
    case 'age':
      return [null] // handled specially below
    case 'email':
      return person.emails
    case 'phone':
      return person.phones
    case 'tag':
      return person.tagIds
    case 'custom': {
      const value = condition.fieldId ? person.custom[condition.fieldId] : null
      return [value ?? null]
    }
  }
}

const isBlank = (value: RawFieldValue): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0)

function compareScalar(
  op: SegmentOperator,
  operand: RawFieldValue,
  value: SegmentValue | undefined,
): boolean {
  switch (op) {
    case 'eq':
      return typeof operand === 'boolean'
        ? operand === (value === true || value === 'true')
        : lower(operand) === lower(value)
    case 'neq':
      return lower(operand) !== lower(value)
    case 'in':
      return asArray(value).some((candidate) => lower(candidate) === lower(operand))
    case 'contains':
      return lower(operand).includes(lower(value))
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      // Dates compare as ISO strings, numbers as numbers — both are ordered
      // the same way the SQL compiler orders them.
      const left = typeof operand === 'number' ? operand : String(operand ?? '')
      const right = typeof left === 'number' ? Number(value) : String(value ?? '')
      if (typeof left === 'number' && Number.isNaN(left)) return false
      if (op === 'gt') return left > right
      if (op === 'gte') return left >= right
      if (op === 'lt') return left < right
      return left <= right
    }
    case 'between': {
      const { from, to } = asRange(value)
      const left = typeof operand === 'number' ? operand : String(operand ?? '')
      if (typeof left === 'number') {
        if (from !== undefined && left < Number(from)) return false
        if (to !== undefined && left > Number(to)) return false
        return true
      }
      if (left === '') return false
      if (from !== undefined && left < String(from)) return false
      if (to !== undefined && left > String(to)) return false
      return true
    }
    case 'isTrue':
      return operand === true
    case 'isFalse':
      return operand === false
    default:
      return false
  }
}

function evaluateCondition(
  condition: SegmentCondition,
  person: PersonSnapshot,
  today: string,
): boolean {
  if (condition.field === 'age') {
    const age = ageOn(person.birthdate, today)
    if (age === null) return condition.op === 'isEmpty'
    if (condition.op === 'isEmpty') return false
    if (condition.op === 'isNotEmpty') return true
    return compareScalar(condition.op, age, condition.value)
  }

  const operands = operandsOf(condition, person)

  if (condition.op === 'isEmpty') return operands.every(isBlank)
  if (condition.op === 'isNotEmpty') return operands.some((operand) => !isBlank(operand))

  if (condition.op === 'has') {
    // Membership in a set: tag ids, or a multiselect custom field's options.
    const wanted = lower(condition.value)
    return operands.some((operand) =>
      Array.isArray(operand)
        ? operand.some((item) => lower(item) === wanted)
        : lower(operand) === wanted,
    )
  }

  // Multi-valued operands (emails, phones, tags) match if any value matches.
  return operands.some((operand) =>
    Array.isArray(operand)
      ? operand.some((item) => compareScalar(condition.op, item, condition.value))
      : compareScalar(condition.op, operand, condition.value),
  )
}

/**
 * Does this person belong to the segment? `today` is explicit so a preview
 * rendered at 23:59 and a server refresh at 00:01 can be made to agree.
 */
export function evaluateSegment(
  node: SegmentNode,
  person: PersonSnapshot,
  today: string = new Date().toISOString().slice(0, 10),
): boolean {
  if (isAllNode(node)) return node.all.every((child) => evaluateSegment(child, person, today))
  if (isAnyNode(node)) return node.any.some((child) => evaluateSegment(child, person, today))
  if (isNotNode(node)) return !evaluateSegment(node.not, person, today)
  return evaluateCondition(node, person, today)
}

// ---- description -------------------------------------------------------------

const OPERATOR_LABELS: Record<SegmentOperator, string> = {
  eq: 'is',
  neq: 'is not',
  in: 'is one of',
  contains: 'contains',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  between: 'is between',
  isEmpty: 'is empty',
  isNotEmpty: 'is present',
  has: 'includes',
  isTrue: 'is yes',
  isFalse: 'is no',
}

const FIELD_LABELS: Record<SegmentField, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  membershipStatus: 'Status',
  campusId: 'Campus',
  birthdate: 'Birthdate',
  age: 'Age',
  createdAt: 'Added',
  email: 'Email',
  phone: 'Phone',
  tag: 'Tag',
  custom: 'Custom field',
}

export const segmentFieldLabel = (field: SegmentField): string => FIELD_LABELS[field]
export const segmentOperatorLabel = (op: SegmentOperator): string => OPERATOR_LABELS[op]

/** Which operators make sense for a field — drives the builder's dropdowns. */
export function operatorsForField(field: SegmentField): SegmentOperator[] {
  switch (field) {
    case 'firstName':
    case 'lastName':
      return ['contains', 'eq', 'isEmpty', 'isNotEmpty']
    case 'membershipStatus':
    case 'campusId':
      return ['eq', 'neq', 'in', 'isEmpty', 'isNotEmpty']
    case 'birthdate':
    case 'createdAt':
      return ['gte', 'lte', 'between', 'isEmpty', 'isNotEmpty']
    case 'age':
      return ['gte', 'lte', 'between', 'isEmpty', 'isNotEmpty']
    case 'email':
    case 'phone':
      return ['isNotEmpty', 'isEmpty', 'contains']
    case 'tag':
      return ['has']
    case 'custom':
      return [
        'eq',
        'contains',
        'has',
        'gte',
        'lte',
        'between',
        'isTrue',
        'isFalse',
        'isEmpty',
        'isNotEmpty',
      ]
  }
}

/** Human summary of a rule tree — list cards and audit entries. */
export function describeSegment(
  node: SegmentNode,
  labels: { tag?: (id: string) => string; campus?: (id: string) => string } = {},
): string {
  if (isAllNode(node)) {
    return node.all.length === 0
      ? 'Everyone'
      : node.all.map((child) => describeSegment(child, labels)).join(' and ')
  }
  if (isAnyNode(node)) {
    return node.any.length === 0
      ? 'Everyone'
      : `(${node.any.map((child) => describeSegment(child, labels)).join(' or ')})`
  }
  if (isNotNode(node)) return `not ${describeSegment(node.not, labels)}`

  const value =
    node.field === 'tag'
      ? (labels.tag?.(String(node.value)) ?? String(node.value))
      : node.field === 'campusId'
        ? (labels.campus?.(String(node.value)) ?? String(node.value))
        : Array.isArray(node.value)
          ? node.value.join(', ')
          : typeof node.value === 'object'
            ? `${asRange(node.value).from ?? '…'}–${asRange(node.value).to ?? '…'}`
            : String(node.value ?? '')

  return `${FIELD_LABELS[node.field]} ${OPERATOR_LABELS[node.op]}${value === '' ? '' : ` ${value}`}`
}
